import { useEffect, useMemo, useState } from 'react';
import type { Analyse, ScoreMarket } from 'r/types';
import { showNotification } from 'r/utils';
import {
  getMarketPredictions,
  getMarketSymbol,
  getScoreError,
  isMarketFrozen,
  isMarketStarted,
  marketBodyFromAnalysis,
  withoutFrozenFields,
  type ScoreMarketBody,
} from 'r/utils/scoreMarket';
import {
  notifyScoreError,
  useCreateScoreMarket,
  useDeleteScoreMarket,
  useUpdateScoreMarket,
} from '../../../Root/App/AdminDashboard/ScoreMarketsTab/mutations';
import {
  formatDateForDescription,
  updateDescriptionLine,
} from '../EventFormModal';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import { MEME_URLS, GOOD_MEMES, BAD_MEMES } from 'r/constants/memes';

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const FOUR_DAYS_MS = 4 * DAY_MS;
const TWO_WEEKS_MS = 14 * DAY_MS;

// Same cadence as existing markets: open on the next full hour,
// predictions close 3 days later, resolve 2 weeks after opening
const getDefaultDates = () => {
  const start = new Date(
    Math.ceil((Date.now() + 5 * 60 * 1000) / HOUR_MS) * HOUR_MS,
  );
  return {
    startDate: start.toISOString(),
    entranceDeadline: new Date(start.getTime() + 3 * DAY_MS).toISOString(),
    endDate: new Date(start.getTime() + TWO_WEEKS_MS).toISOString(),
  };
};

const withDateLines = (
  desc: string,
  entranceDeadline: string,
  endDate: string,
) =>
  updateDescriptionLine(
    updateDescriptionLine(
      desc,
      'Entry Deadline',
      formatDateForDescription(entranceDeadline),
    ),
    'Settlement date',
    formatDateForDescription(endDate),
  );

interface ScoreMarketFormModalProps {
  mode: 'create' | 'edit';
  analysis?: Analyse;
  market?: ScoreMarket;
  onClose: () => void;
}

export default function ScoreMarketFormModal({
  mode,
  analysis,
  market,
  onClose,
}: ScoreMarketFormModalProps) {
  const { mutateAsync: createMarket, isPending: isCreating } =
    useCreateScoreMarket();
  const { mutateAsync: updateMarket, isPending: isUpdating } =
    useUpdateScoreMarket();
  const { mutate: deleteMarket, isPending: isDeleting } =
    useDeleteScoreMarket();
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const [form, setForm] = useState(() => {
    if (mode === 'edit' && market) {
      return {
        title: market.title ?? '',
        description: market.description ?? '',
        imageUrl: market.imageUrl,
        startDate: market.startedAt,
        entranceDeadline: market.votingEndsAt,
        endDate: market.resolvesAt,
      };
    }
    const dates = getDefaultDates();
    const direction = analysis?.isPredictionUp ? 'Bullish' : 'Bearish';
    const asset = analysis?.pair.split('/')[0] ?? '';
    return {
      title: analysis ? `${direction} for $${asset}?` : '',
      description: withDateLines('', dates.entranceDeadline, dates.endDate),
      imageUrl: null as string | null,
      ...dates,
    };
  });
  const { title, description, imageUrl, startDate, entranceDeadline, endDate } =
    form;
  const set = (patch: Partial<typeof form>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  // Keep "Entry Deadline" / "Settlement date" lines in sync with the dates
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      description: withDateLines(
        prev.description,
        prev.entranceDeadline,
        prev.endDate,
      ),
    }));
  }, [entranceDeadline, endDate]);

  const isStarted = !!market && isMarketStarted(market);
  const isFrozen = !!market && isMarketFrozen(market);
  const tweetDate = analysis?.tweetDate ?? market?.tweetDate ?? null;
  const isTweetTooOld =
    mode === 'create' &&
    !!tweetDate &&
    Date.now() - new Date(tweetDate).getTime() > FOUR_DAYS_MS;

  const validationErrors = useMemo(() => {
    const errors: string[] = [];
    if (!title.trim()) errors.push('Title is required');
    if (!description.trim()) errors.push('Description is required');
    if (isFrozen) return errors;

    const start = new Date(startDate);
    const deadline = new Date(entranceDeadline);
    const end = new Date(endDate);
    if (!(start <= deadline && deadline <= end))
      errors.push('Dates must go start ≤ entrance deadline ≤ end');
    // A started market keeps its past start date; only a new one is checked
    if (startDate !== market?.startedAt && start < new Date())
      errors.push('Start date cannot be in the past');
    if (end.getTime() - start.getTime() > TWO_WEEKS_MS)
      errors.push('End date must be within 2 weeks of start date');
    if (mode === 'create' && tweetDate) {
      const tweet = new Date(tweetDate);
      if (start < tweet) errors.push('Start date cannot be before the tweet');
      else if (start.getTime() - tweet.getTime() > FOUR_DAYS_MS)
        errors.push('Start date must be within 4 days of the tweet');
    }
    return errors;
  }, [
    mode,
    title,
    description,
    startDate,
    entranceDeadline,
    endDate,
    tweetDate,
    market,
    isFrozen,
  ]);

  const textsBody: ScoreMarketBody = {
    title,
    description,
    imageUrl: imageUrl || null,
  };
  // On edit only changed dates are sent, so a past start date is never resent
  const datesBody: ScoreMarketBody = {
    ...(startDate !== market?.startedAt && { startDate }),
    ...(entranceDeadline !== market?.votingEndsAt && { entranceDeadline }),
    ...(endDate !== market?.resolvesAt && { endDate }),
  };

  const handleCreate = async () => {
    if (!analysis) return;
    const body = {
      ...marketBodyFromAnalysis(analysis),
      ...textsBody,
      ...datesBody,
    };
    try {
      await createMarket(body);
      showNotification('success', 'Score market created');
      onClose();
    } catch (e) {
      const { code, details } = getScoreError(e);
      // The analysis had a market that was deleted: bring it back instead
      if (code === 'MARKET_DELETED' && typeof details.marketId === 'string') {
        try {
          await updateMarket({
            marketId: details.marketId,
            body: { ...body, deletedAt: null },
          });
          showNotification('success', 'Deleted Score market restored');
          onClose();
        } catch (restoreError) {
          notifyScoreError(restoreError, 'Failed to restore market');
        }
        return;
      }
      notifyScoreError(e, 'Failed to create market');
    }
  };

  const handleUpdate = async () => {
    if (!market) return;
    try {
      await updateMarket({
        marketId: market.id,
        body: withoutFrozenFields({ ...textsBody, ...datesBody }, market),
      });
      showNotification('success', 'Score market updated');
      onClose();
    } catch (e) {
      notifyScoreError(e, 'Failed to update market');
    }
  };

  const handleDelete = () => {
    if (!market) return;
    if (!isConfirmingDelete) {
      setIsConfirmingDelete(true);
      return;
    }
    deleteMarket(market.id, { onSuccess: onClose });
  };

  if (mode === 'edit' && !market) return null;
  if (mode === 'create' && !analysis) return null;

  const kolName = analysis?.authorName ?? market?.kol.name;
  const kolHandle = analysis?.authorUsername ?? market?.kol.handle;
  const symbol = analysis?.pair ?? (market && getMarketSymbol(market));
  const isUp = analysis?.isPredictionUp ?? market?.direction === 'up';
  const tweetText = analysis?.tweetText ?? market?.tweetText;
  const isSubmitting = isCreating || isUpdating;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="sm:max-w-3xl max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className="text-lg font-semibold">
          {mode === 'create' ? 'Add Score market' : 'Edit Score market'}
        </DialogTitle>

        <div className="flex flex-col gap-6 mt-4">
          <div className="flex flex-col gap-2 p-4 border border-border rounded-lg">
            <div>
              <p className="font-medium">{kolName}</p>
              {kolHandle && (
                <p className="text-sm text-(--textSecondary)">@{kolHandle}</p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-2 py-1 text-xs bg-accent rounded">
                {symbol}
              </span>
              <span className="px-2 py-1 text-xs bg-accent rounded">
                {isUp ? 'Up' : 'Down'}
              </span>
              {market && (
                <span className="px-2 py-1 text-xs bg-accent rounded">
                  {getMarketPredictions(market)} predictions
                </span>
              )}
            </div>
            {tweetText && (
              <p className="text-sm whitespace-pre-wrap">{tweetText}</p>
            )}
            {tweetDate && (
              <p className="text-xs text-(--textSecondary)">
                Tweet: {new Date(tweetDate).toLocaleString()}
              </p>
            )}
          </div>

          {(isFrozen || isStarted) && (
            <div className="rounded-md bg-yellow-500/10 border border-yellow-500/30 px-4 py-3 text-sm text-yellow-600 dark:text-yellow-400">
              {isFrozen ?
                'Market has predictions: KOL, pair, direction, tweet and dates are locked. Title, description and meme can still be changed.'
              : 'Market has started: it can still be edited, but no longer deleted.'
              }
            </div>
          )}

          {isTweetTooOld && (
            <div className="rounded-md bg-yellow-500/10 border border-yellow-500/30 px-4 py-3 text-sm text-yellow-600 dark:text-yellow-400">
              More than 4 days have passed since the tweet. Markets can only be
              created within 4 days of the tweet.
            </div>
          )}

          <div className="flex flex-col gap-4">
            <div className="space-y-2">
              <Label htmlFor="score-market-title">
                Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="score-market-title"
                value={title}
                onChange={(e) => set({ title: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="score-market-description">
                Description <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="score-market-description"
                value={description}
                onChange={(e) => set({ description: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <DateTimePicker
                  value={new Date(startDate)}
                  onChange={(date) =>
                    date && set({ startDate: date.toISOString() })
                  }
                  disabled={isFrozen}
                />
              </div>
              <div className="space-y-2">
                <Label>Entrance Deadline</Label>
                <DateTimePicker
                  value={new Date(entranceDeadline)}
                  onChange={(date) =>
                    date && set({ entranceDeadline: date.toISOString() })
                  }
                  minDate={new Date(startDate)}
                  maxDate={new Date(endDate)}
                  disabled={isFrozen}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <DateTimePicker
                  value={new Date(endDate)}
                  onChange={(date) =>
                    date && set({ endDate: date.toISOString() })
                  }
                  minDate={new Date(startDate)}
                  maxDate={
                    new Date(new Date(startDate).getTime() + TWO_WEEKS_MS)
                  }
                  disabled={isFrozen}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <h3 className="text-sm font-bold uppercase text-(--textSecondary)">
              Meme
            </h3>
            {[GOOD_MEMES, BAD_MEMES].map((row, rowIdx) => (
              <div key={rowIdx} className="grid grid-cols-7 gap-2">
                {row.map((name) => {
                  const isSelected = imageUrl === MEME_URLS[name];
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() =>
                        set({ imageUrl: isSelected ? null : MEME_URLS[name] })
                      }
                      className={`rounded border-2 overflow-hidden transition-all ${
                        isSelected ?
                          'border-blue-500 opacity-100'
                        : 'border-transparent opacity-40 hover:opacity-70'
                      }`}
                    >
                      <img
                        src={MEME_URLS[name]}
                        alt={name}
                        className="w-full aspect-square object-cover"
                      />
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {mode === 'edit' && !isStarted && (
            <div>
              <h3 className="font-semibold mb-2 text-red-500">Danger Zone</h3>
              <div className="flex flex-col gap-2">
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isConfirmingDelete ?
                    'Click again to confirm DELETE'
                  : 'DELETE FROM SCORE'}
                </Button>
                {isConfirmingDelete && (
                  <Button
                    variant="outline"
                    onClick={() => setIsConfirmingDelete(false)}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3">
            {validationErrors.length > 0 && (
              <div className="rounded-md bg-red-500/10 border border-red-500/20 px-3 py-2">
                <ul className="text-sm text-red-500 space-y-0.5">
                  {validationErrors.map((err) => (
                    <li key={err} className="flex items-start gap-1.5">
                      <span className="mt-0.5 shrink-0">•</span>
                      <span>{err}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end items-center gap-3">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={mode === 'create' ? handleCreate : handleUpdate}
                disabled={
                  isSubmitting || validationErrors.length > 0 || isTweetTooOld
                }
              >
                {isSubmitting ?
                  'Saving...'
                : mode === 'create' ?
                  'Create market'
                : 'Save changes'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
